<script lang="ts">
  import { type PlayerInterface } from '@foundation-player/loader';
  import { getContext } from 'svelte';
  import SpeakerMute from '../assets/icons/SpeakerMute.svelte';
  import SpeakerUnmute from '../assets/icons/SpeakerUnmute.svelte';
  import type { FocusItem, FocusLayer } from '../focus-manager/focus-types';
  import type { PlayerStateContext } from '../types/player-state';
  import type { SliderState } from '../types/slider';
  import type { VolumeComponentMode, VolumeOrientation } from '../types/volume';
  import IconButton from './IconButton.svelte';
  import Slider from './Slider.svelte';

  export let player: PlayerInterface;
  export let mode: VolumeComponentMode = 'full';
  export let orientation: VolumeOrientation = 'horizontal';
  export let focusLayer: FocusLayer;
  export let focusName: FocusItem;

  const { muted, volume } = getContext<PlayerStateContext>('playerState');

  const toggleMute = () => {
    // Use the current player instead of $muted value to be more robust.
    if (player.isMuted()) {
      player.unmute();
    } else {
      player.mute();
    }
  };

  const setVolume = (event: CustomEvent<SliderState>) => {
    if ($volume !== event.detail.value) {
      player.setVolume(event.detail.value);
    }
  };

  let sliding = false;

  const startSliding = () => {
    sliding = true;
  };

  const stopSliding = () => {
    sliding = false;
  };

  const getValueText = (current: number) => `${Math.round(100 * current)}%`;
</script>

<div class="volume-wrapper" class:orientation-vertical={orientation === 'vertical'} class:active={sliding}>
  <IconButton
    on:click={toggleMute}
    title={$muted ? 'Stummschaltung aufheben' : 'Stummschalten'}
    {focusLayer}
    {focusName}
  >
    {#if $muted}
      <SpeakerMute aria-hidden={true} />
    {:else}
      <SpeakerUnmute aria-hidden={true} />
    {/if}
  </IconButton>

  {#if mode === 'full'}
    <div class="volumebar-wrapper">
      <Slider
        {orientation}
        id="VolumeSlider"
        min={0}
        max={1}
        step={0.01}
        current={$volume}
        on:change={setVolume}
        on:startSliding={startSliding}
        on:stopSliding={stopSliding}
        label="Lautstärke"
        {getValueText}
        {focusLayer}
        focusName="volumeSlider"
      />
    </div>
  {/if}
</div>

<style module>
  .volume-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    position: relative;
    padding-right: 8px;
  }

  .volume-wrapper.orientation-vertical {
    flex-direction: column-reverse;
    gap: 0;
    padding-right: 0;
  }

  .volume-wrapper:hover {
    /* no need for fallback for old browsers, because the volume slider is not
     * shown at all in old browsers, since we use container queries for display
     */
    background-color: var(--icon-hover-background-color);
    color: var(--icon-hover-color);

    --icon-color: var(--icon-hover-color);
    --icon-background-color: transparent;

    --slider-bar-color: #808080;
    --slider-hover-bar-color: #808080;
    --slider-track-color: var(--focus-color);
    --slider-hover-track-color: var(--focus-color);
    --slider-knob-color: var(--focus-color);
    --slider-hover-knob-color: var(--focus-color);
  }

  :global([data-mode='audio']) .volume-wrapper:hover {
    --outline: 2px solid #ffffff;
  }

  :global([data-mode='audio'][data-theme='dark']) .volume-wrapper:hover {
    --outline: 2px solid #000000;
  }

  .volume-wrapper:hover :global(button) {
    --icon-hover-background-color: transparent;
  }

  .volumebar-wrapper {
    display: none;
    width: 7em;
    padding: 0 14px 0 6px;
    align-items: center;
    justify-content: center;
  }

  .volume-wrapper.orientation-vertical .volumebar-wrapper {
    width: 100%;
    height: 7em;
    padding: 14px 0;
    margin-top: 8px;
  }

  /* only show volumebar-slider on devices where the main input can hover */
  @media (hover: hover) {
    /* horizontal orientation: show independent of available space */
    .volume-wrapper.orientation-vertical .volumebar-wrapper {
      display: flex;
      opacity: 0;
      visibility: hidden;
    }

    .volume-wrapper.orientation-vertical.active .volumebar-wrapper,
    .volume-wrapper.orientation-vertical:focus-within:has(:focus-visible) .volumebar-wrapper,
    .volume-wrapper.orientation-vertical:hover .volumebar-wrapper {
      opacity: 1;
      visibility: visible;
    }

    /* horizontal orientation: hover or focus on the mute-button and when there is enough space */
    @container player-wrapper (min-width: 500px) {
      .volumebar-wrapper {
        display: flex;
        opacity: 0;
        visibility: hidden;
      }

      .volume-wrapper.active .volumebar-wrapper,
      .volume-wrapper:focus-within:has(:focus-visible) .volumebar-wrapper,
      .volume-wrapper:hover .volumebar-wrapper {
        opacity: 1;
        visibility: visible;
      }
    }
  }

  @media (prefers-reduced-motion: no-preference) {
    .volume-wrapper,
    .volumebar-wrapper {
      transition: all 0.3s ease-in-out;
    }
  }
</style>
